/**
 * FokusDropdownMenu - The dropdown menu content for FokusButton
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { PRIORITY_OPTIONS, DUE_DATE_OPTIONS } from '../../filterUtils';
import QuickActions from './QuickActions';
import FokusFilterSection from './FokusFilterSection';
import FokusFilterOption from './FokusFilterOption';

const FokusDropdownMenu = ({
  menuRef,
  menuPosition,
  // Quick actions
  hasSelection,
  selectedCount,
  selectionLabel,
  actionableTaskIds,
  onFocusSelected,
  onActionableClick,
  clearAllFilters,
  setIsOpen,
  // Filter state
  activeFilterCount,
  matchingTaskIds,
  expandedSections,
  toggleSection,
  // Status
  statusOptions,
  selectedStatuses,
  toggleStatus,
  // Categories
  taskCategories,
  selectedCategories,
  toggleCategory,
  // Tags
  taskTags,
  selectedTags,
  toggleTag,
  // Priority
  selectedPriorities,
  togglePriority,
  // Due Date
  selectedDueDates,
  toggleDueDate,
}) => {
  return createPortal(
    <div 
      className="fokus-button__menu"
      ref={menuRef}
      style={{ top: menuPosition.top, left: menuPosition.left }}
    >
      <div className="fokus-button__menu-header">
        <span className="fokus-button__menu-title">Fokus On...</span>
        {activeFilterCount > 0 && (
          <button className="fokus-button__menu-clear" onClick={clearAllFilters}>
            Clear
          </button>
        )}
      </div>

      {/* Quick Actions */}
      <QuickActions
        hasSelection={hasSelection}
        selectedCount={selectedCount}
        selectionLabel={selectionLabel}
        actionableTaskIds={actionableTaskIds}
        onFocusSelected={onFocusSelected}
        onActionableClick={onActionableClick}
        clearAllFilters={clearAllFilters}
        setIsOpen={setIsOpen}
      />

      <div className="fokus-button__divider" />

      {/* Status */}
      {statusOptions.length > 0 && (
        <FokusFilterSection 
          title="Status" 
          sectionId="status" 
          expandedSections={expandedSections} 
          toggleSection={toggleSection}
          count={selectedStatuses.size}
        >
          {statusOptions.map(status => (
            <FokusFilterOption
              key={status.id}
              label={status.name}
              color={status.color}
              checked={selectedStatuses.has(status.id)}
              onChange={() => toggleStatus(status.id)}
            />
          ))}
        </FokusFilterSection>
      )}

      {/* Primary Tag */}
      {taskCategories.length > 0 && (
        <FokusFilterSection 
          title="Primary Tag" 
          sectionId="category" 
          expandedSections={expandedSections} 
          toggleSection={toggleSection}
          count={selectedCategories.size}
        >
          {taskCategories.map(cat => (
            <FokusFilterOption
              key={cat.name}
              label={cat.name}
              color={cat.color}
              checked={selectedCategories.has(cat.name)}
              onChange={() => toggleCategory(cat.name)}
            />
          ))}
        </FokusFilterSection>
      )}

      {/* Tags */}
      {taskTags.length > 0 && (
        <FokusFilterSection 
          title="Tags" 
          sectionId="tags" 
          expandedSections={expandedSections} 
          toggleSection={toggleSection}
          count={selectedTags.size}
        >
          {taskTags.map(tag => (
            <FokusFilterOption
              key={tag.name}
              label={tag.name}
              color={tag.color}
              checked={selectedTags.has(tag.name)}
              onChange={() => toggleTag(tag.name)}
              isTag
            />
          ))}
        </FokusFilterSection>
      )}

      {/* Priority */}
      <FokusFilterSection 
        title="Priority" 
        sectionId="priority" 
        expandedSections={expandedSections} 
        toggleSection={toggleSection}
        count={selectedPriorities.size}
      >
        {PRIORITY_OPTIONS.map(option => (
          <FokusFilterOption
            key={option.id}
            label={option.label}
            color={option.color}
            checked={selectedPriorities.has(option.id)}
            onChange={() => togglePriority(option.id)}
          />
        ))}
      </FokusFilterSection>

      {/* Due Date */}
      <FokusFilterSection 
        title="Due Date" 
        sectionId="dueDate" 
        expandedSections={expandedSections} 
        toggleSection={toggleSection}
        count={selectedDueDates.size}
      >
        {DUE_DATE_OPTIONS.map(option => (
          <FokusFilterOption
            key={option.id}
            label={option.label}
            color={option.color}
            checked={selectedDueDates.has(option.id)}
            onChange={() => toggleDueDate(option.id)}
          />
        ))}
      </FokusFilterSection>

      {/* Live status indicator */}
      {activeFilterCount > 0 && (
        <div className="fokus-button__menu-footer">
          <span className="fokus-button__live-status">
            {matchingTaskIds.length > 0 
              ? `Fokusing on ${matchingTaskIds.length} task${matchingTaskIds.length !== 1 ? 's' : ''}`
              : 'No matching tasks'
            }
          </span>
        </div>
      )}
    </div>,
    document.body
  );
};

export default FokusDropdownMenu;
